import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Paper, Stack, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useAuth } from "../../providers/AuthProvider";
import api from "../../services/api/axios";
import { BRAND_BLUE } from "../../theme/colors";

const STATUSES = {
  pending: { label: "Pending", color: "warning" },
  accepted: { label: "Accepted", color: "success" },
  expired: { label: "Expired", color: "default" },
  revoked: { label: "Revoked", color: "error" },
};
function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
}
function creationError(error) {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;
  if (status === 409 && detail === "Email already registered") return "This email already belongs to an existing account.";
  if (status === 409 && detail === "An active invitation already exists") return "An active invitation has already been sent to this email.";
  if (status === 502) return "The invitation could not be delivered. Please try again.";
  if (status === 422) return "Enter a valid email address.";
  if (status === 401 || status === 403) return "You do not have permission to manage invitations. Please sign in with a global admin account.";
  return "The invitation could not be sent. Please try again.";
}

// A keyed child clears all tenant-specific state when the selection changes.
export default function AdminOrgInvitations({ organizationId, organizationName }) {
  const { user } = useAuth();
  if (!user?.is_admin || !organizationId) return null;
  return <OrganizationInvitations key={organizationId} organizationId={organizationId} organizationName={organizationName} />;
}

function OrganizationInvitations({ organizationId, organizationName }) {
  const [invitations, setInvitations] = useState([]);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [listError, setListError] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const alive = useRef(false);
  const submitLock = useRef(false);
  const requestNumber = useRef(0);
  const endpoint = `/admin/organizations/${organizationId}/invitations`;

  const load = useCallback(async () => {
    const request = ++requestNumber.current;
    setLoading(true);
    setListError("");
    try {
      const { data } = await api.get(endpoint);
      if (alive.current && request === requestNumber.current) setInvitations(data);
    } catch (failure) {
      if (alive.current && request === requestNumber.current) {
        const status = failure?.response?.status;
        setListError(status === 401 || status === 403
          ? "You do not have permission to view invitations. Please sign in with a global admin account."
          : "Invitations could not be loaded. Please try again.");
      }
    } finally {
      if (alive.current && request === requestNumber.current) setLoading(false);
    }
  }, [endpoint]);

  useEffect(() => {
    alive.current = true;
    load();
    return () => { alive.current = false; requestNumber.current += 1; };
  }, [load]);

  const submit = async event => {
    event.preventDefault();
    if (submitLock.current || loading) return;
    setError("");
    setSuccess("");
    const recipient = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipient)) {
      setError("Enter a valid email address.");
      return;
    }
    submitLock.current = true;
    setBusy(true);
    try {
      const { data } = await api.post(endpoint, { email: recipient });
      if (!alive.current) return;
      // Preserve the API's newest-first ordering and show the returned row immediately.
      setInvitations(previous => [data, ...previous.filter(item => item.id !== data.id)]);
      setEmail("");
      setSuccess(`Invitation sent to ${data.email}`);
    } catch (failure) {
      if (alive.current) setError(creationError(failure));
    } finally {
      submitLock.current = false;
      if (alive.current) setBusy(false);
    }
  };

  return (
    <Paper component="section" aria-labelledby="organization-invitations-heading"
      sx={{ p: 2, minWidth: 0, border: `2px solid ${BRAND_BLUE}`, borderRadius: 4 }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }}>
        <Box>
          <Typography id="organization-invitations-heading" variant="h6" sx={{ color: BRAND_BLUE }}>Invitations</Typography>
          <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>{organizationName}</Typography>
        </Box>
        <Button onClick={load} disabled={loading || busy} startIcon={<RefreshIcon />}>Refresh invitations</Button>
      </Stack>
      <Box component="form" noValidate onSubmit={submit} sx={{ mt: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start">
          <TextField id="admin-invitation-email" label="Email address" type="email" autoComplete="email" required fullWidth
            value={email} onChange={event => setEmail(event.target.value)} disabled={busy}
            error={error === "Enter a valid email address."} />
          <Button type="submit" variant="contained" disabled={busy || loading || !email.trim()}
            sx={{ minHeight: 56, whiteSpace: "nowrap", width: { xs: "100%", sm: "auto" } }}>
            {busy ? "Sending…" : "Send Invitation"}
          </Button>
        </Stack>
      </Box>
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
      {listError && <Alert severity="error" sx={{ mt: 2 }}>{listError}</Alert>}
      {loading ? <Stack role="status" direction="row" spacing={1} alignItems="center" sx={{ mt: 3 }}><CircularProgress size={20} /><Typography>Loading invitations…</Typography></Stack> : (
        <TableContainer sx={{ mt: 2 }}>
          <Table size="small" aria-label="Organization invitations" sx={{ minWidth: 760 }}>
            <TableHead><TableRow>{["Email", "Status", "Sent", "Expires", "Accepted"].map(label => <TableCell key={label} sx={{ color: BRAND_BLUE }}>{label}</TableCell>)}</TableRow></TableHead>
            <TableBody>
              {invitations.map(invitation => {
                const status = STATUSES[invitation.status] || { label: "Unknown", color: "default" };
                return <TableRow key={invitation.id}>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{invitation.email}</TableCell>
                  <TableCell><Chip size="small" label={status.label} color={status.color} /></TableCell>
                  <TableCell>{formatDate(invitation.created_at)}</TableCell>
                  <TableCell>{formatDate(invitation.expires_at)}</TableCell>
                  <TableCell>{formatDate(invitation.accepted_at)}</TableCell>
                </TableRow>;
              })}
              {!invitations.length && !listError && <TableRow><TableCell colSpan={5} align="center">No invitations have been sent for this organization yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}
